import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  FileText,
  Search,
  GitCompare,
  Award,
  ListChecks,
  CheckCircle2,
  Star,
  ArrowRight,
  Bot,
} from "lucide-react";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Use Radius as a Buyer",
  "description": "Step-by-step guide for buyers to manage procurement from RFQ creation to payment.",
  "step": [
    {
      "@type": "HowToStep",
      "name": "Create Your Account & Profile",
      "text": "Sign up and complete your buyer profile with company information and payment setup."
    },
    {
      "@type": "HowToStep",
      "name": "Build Your RFQ with AI",
      "text": "Describe your project in plain English and let AI guide you through creating professional specifications."
    },
    {
      "@type": "HowToStep",
      "name": "Invite or Discover Vendors",
      "text": "Make your RFQ public or invite specific vendors with the right certifications and capabilities."
    },
    {
      "@type": "HowToStep",
      "name": "Review & Compare Bids",
      "text": "Receive detailed bids and compare them side-by-side with line-item breakdowns."
    },
    {
      "@type": "HowToStep",
      "name": "Award Job & Set Up Payment",
      "text": "Select the winning bid and secure payment in escrow through Stripe."
    },
    {
      "@type": "HowToStep",
      "name": "Track Milestones & Progress",
      "text": "Monitor project progress through milestone tracking and built-in communication."
    },
    {
      "@type": "HowToStep",
      "name": "Verify Delivery & Approve Payment",
      "text": "Review proof of delivery and release payment upon satisfaction."
    },
    {
      "@type": "HowToStep",
      "name": "Leave a Review",
      "text": "Rate your experience to help other buyers and build vendor reputation."
    }
  ]
};

export default function HowItWorksClients() {
  const steps = [
    {
      number: 1,
      icon: FileText,
      title: "Create Your Account & Profile",
      description: "Sign up and complete your buyer profile. Add your company information, contact details, and set up your payment method through Stripe.",
      details: [
        "Quick signup process takes less than 5 minutes",
        "Secure payment setup with Stripe",
        "Optional organization profiles for team management",
      ]
    },
    {
      number: 2,
      icon: Bot,
      title: "Build Your RFQ with AI",
      description: "Describe your project in plain English and our AI assistant guides you through creating professional specifications. Only fill out a form if that's your preference.",
      details: [
        "Conversational interface—just describe what you need",
        "AI asks smart follow-up questions for key details",
        "Automatic formatting into industry-standard specs",
        "Review and refine before publishing",
        "Upload supporting documents and drawings",
        "Save as template for future use",
      ]
    },
    {
      number: 3,
      icon: Search,
      title: "Invite or Discover Vendors",
      description: "Choose to make your RFQ public or invite specific vendors. Use our discovery tools to browse vendors with documented certifications and capabilities.",
      details: [
        "Search vendors by capability, certification, and location",
        "Review vendor profiles, ratings, and order history",
        "Invite specific vendors you've worked with before",
        "Or publish publicly to receive bids from enrolled vendors",
      ]
    },
    {
      number: 4,
      icon: GitCompare,
      title: "Review & Compare Bids",
      description: "Receive detailed bids from interested vendors. Compare them side-by-side with line-item breakdowns and vendor profiles.",
      details: [
        "View all bids in unified comparison interface",
        "Line-item pricing with quantity break details",
        "Review vendor certifications and past reviews",
        "Communicate with vendors to clarify questions",
        "Decline bids that don't meet requirements",
      ]
    },
    {
      number: 5,
      icon: Award,
      title: "Award Job & Set Up Payment",
      description: "Select the winning bid and confirm the purchase agreement. Your payment is secured in escrow through Stripe.",
      details: [
        "Review final pricing and delivery terms",
        "Sign digital purchase agreement",
        "Payment secured immediately in escrow",
        "Vendor is notified and can begin work",
      ]
    },
    {
      number: 6,
      icon: ListChecks,
      title: "Track Milestones & Progress",
      description: "Monitor project progress through milestone tracking. Communicate with vendor, track shipments, and manage all documentation.",
      details: [
        "Real-time visibility into milestone completion",
        "Built-in messaging with vendor",
        "Track multiple shipments with carrier info",
        "Upload and organize project documents",
        "Receive notifications for important updates",
      ]
    },
    {
      number: 7,
      icon: CheckCircle2,
      title: "Verify Delivery & Approve Payment",
      description: "Review vendor-submitted proof of delivery for each milestone. Once satisfied, approve and payment is automatically released.",
      details: [
        "Vendor submits delivery proof with photos and documents",
        "Review against agreed-upon deliverables",
        "Request changes if needed",
        "Approve to automatically release payment",
        "Dispute resolution available if issues arise",
      ]
    },
    {
      number: 8,
      icon: Star,
      title: "Leave a Review",
      description: "Rate your experience and leave feedback to help other buyers. Your review builds the vendor's platform history.",
      details: [
        "Rate overall experience (1-5 stars)",
        "Rate specific aspects: quality, communication, timeliness",
        "Leave detailed written feedback",
        "Help build transparent vendor history through reviews",
      ]
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="How It Works for Buyers"
        description="Step-by-step guide for buyers: from AI-powered RFQ creation to secure payment. Learn how to manage your procurement on Radius."
        canonical="/how-it-works/clients"
        structuredData={howToSchema}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            How It Works for <span className="text-primary">Buyers</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            A simple, step-by-step journey from creating your RFQ to receiving quality work and secure payment processing.
          </p>
          <Button size="lg" asChild>
            <Link to="/auth/signup?type=buyer">
              Get Started as Buyer <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="container py-20">
        <div className="max-w-4xl mx-auto space-y-16">
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              {/* Connector Line */}
              {index !== steps.length - 1 && (
                <div className="absolute left-6 top-20 bottom-0 w-0.5 bg-border -translate-x-1/2 hidden md:block" />
              )}
              
              <div className="flex flex-col md:flex-row gap-6">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
                      {step.number}
                    </div>
                    <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                      <step.icon className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                  
                  <ul className="space-y-2">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Summary */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">In Summary</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Complete Control</h3>
              <p className="text-sm text-muted-foreground">
                You control every step from vendor selection to payment approval. Nothing happens without your explicit authorization.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Payment Protection</h3>
              <p className="text-sm text-muted-foreground">
                Your payment is held securely with purchase protection and only released when you verify delivery and approve.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Full Transparency</h3>
              <p className="text-sm text-muted-foreground">
                Track every detail of your project with real-time updates, built-in communication, and comprehensive documentation.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Informed Decision-Making</h3>
              <p className="text-sm text-muted-foreground">
                Review vendor profiles with documented capabilities, compare detailed proposals, and leverage community reviews to make informed decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Ready to Streamline Your Procurement?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of companies managing their procurement process with confidence and transparency.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=buyer">
                Create Your Account
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/for-clients">
                Learn More About Benefits
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
